import { PricingType, PricingTypes, ProjectType, ProjectTypes } from "../../../common/constants"
import { CompletedWorkareaVisit, CreateInvoiceRequest } from "../../../domain/invoicing/invoicingApi"
import { InvoiceSummary } from "../../../domain/invoicing/invoice"
import { ResolvedTaxRate } from "../../../domain/invoicing/resolvedTaxRateApi"
import { Transaction } from "../../../domain/invoicing/transaction"
import { Workarea } from "../../../domain/job"
import { ActualItem, EstimatedItem, WorkareaItemBase, isActualItem } from "../../../domain/workareaItem"
import { WorkareaGroupWithWorkareas } from "../../../domain/workareas/group"
import { sum } from "../../../helpers/array"
import { hasValue } from "../../../helpers/nullable"
import { isEmptyRichText } from "../../RichTextEditor/richTextModel"
import { InvoicingSettings, InvoicingSystem, LineItemGroupingOption } from "../../../domain/invoicing/settings/invoicingSettings"

export type InvoiceWorkareaGroup = {
  id: string
  name: string
  showTaxInFinalPrice: boolean
  selected: boolean
  amount: number
  perVisitAmount: number
  pricingType: PricingType
  billableAmount: number
  unbilledAmount: number
  alreadyInvoiced: number
  description: string
  proposalDescription: string
  includeItemsSummary: boolean
  itemGroupingOption: number
  allowLineItemPerGroup: boolean
  allowLineItemPerWorkareaItem: boolean
  deductionAmount: number
  includeDeduction: boolean
  selectedActualItemIds: number[]
  selectedVisitLaborIds: string[]
  selectedItemGuids: string[]
  selectedVisitIds: string[]
  workareaIds: number[]
  selectedSalesTaxId?: number
  exemptSalesTaxId?: number
  taxRateId?: string | null
  workareas: PerWorkareaData[]
  hidden: boolean
  completedVisits: CompletedWorkareaVisit[]
}

export type NewInvoiceData = {
  workareaGroups: InvoiceWorkareaGroup[]
  enableACHPayments: boolean
  enableCreditCardPayments: boolean
  showAuthorizePaymentOnFile: boolean
  requireAuthorizePaymentOnFile: boolean
  includePaymentSummary: boolean
  dueDate: string
}

export type PerWorkareaData = {
  workareaId: number
  name: string
  maxBillableAmount: number
  alreadyInvoiced: number
  quotedPrice: number
  amount: number
  deductionAmount: number
}

export const defaultInvoiceWorkareaGroup = (): InvoiceWorkareaGroup => ({
  id: '',
  name: '',
  showTaxInFinalPrice: false,
  selected: false,
  amount: 0,
  perVisitAmount: 0,
  pricingType: PricingTypes.QuotedPrice,
  billableAmount: 0,
  unbilledAmount: 0,
  alreadyInvoiced: 0,
  description: '',
  proposalDescription: '',
  includeItemsSummary: false,
  itemGroupingOption: 0,
  allowLineItemPerGroup: false,
  allowLineItemPerWorkareaItem: false,
  deductionAmount: 0,
  includeDeduction: false,
  selectedActualItemIds: [],
  selectedVisitLaborIds: [],
  selectedItemGuids: [],
  selectedVisitIds: [],
  workareaIds: [],
  taxRateId: null,
  workareas: [],
  hidden: false,
  completedVisits: []
})

export const createInvoiceWorkareaGroups = (
  groups: WorkareaGroupWithWorkareas[],
  invoiceSummaries: InvoiceSummary[],
  transactions: Transaction[],
  estimatedItems: EstimatedItem[],
  actualItems: ActualItem[],
  defaultSalesTaxId: number | undefined,
  defaultExemptSalesTaxId: number | undefined,
  completedWorkareaVisits: CompletedWorkareaVisit[],
  defaultGroupingOption: LineItemGroupingOption
): InvoiceWorkareaGroup[] => {
  return groups
    .filter(x => x.isApproved)
    .map(x => createInvoiceWorkareaGroup(x, invoiceSummaries, transactions, estimatedItems, actualItems, completedWorkareaVisits, defaultGroupingOption, defaultSalesTaxId, defaultExemptSalesTaxId))
    .filter(x => x.unbilledAmount > 0)
}

const getValidGroupingOptions = (pricingType: PricingType, isHiddenGroup: boolean): LineItemGroupingOption[] => {
  switch (pricingType) {
    case PricingTypes.TimeAndMaterials:
    case PricingTypes.FlatRate:
      return isHiddenGroup
        ? ['perWorkarea', 'perWorkareaAndVisit', 'perWorkareaItem']
        : ['perWorkarea', 'perWorkareaAndVisit', 'perWorkareaItem', 'perGroup']
    case PricingTypes.PerVisit:
      return ['perWorkarea', 'perWorkareaAndVisit']
    default:
      return ['perWorkarea']
  }
}

const mapGroupingOptionToNumber = (groupingOption: LineItemGroupingOption): number => {
  switch (groupingOption) {
    case 'perWorkarea':
      return 1
    case 'perWorkareaAndVisit':
      return 2
    case 'perWorkareaItem':
      return 3
    case 'perGroup':
      return 4
    default:
      return 1
  }
}

export const getValidGroupingOption = (invoiceGroupingOption: LineItemGroupingOption, pricingType: PricingType, isHiddenGroup: boolean): number => {
  const validOptions = getValidGroupingOptions(pricingType, isHiddenGroup)
  const valid = validOptions.includes(invoiceGroupingOption) ? invoiceGroupingOption : validOptions[0]
  return mapGroupingOptionToNumber(valid)
}

export const createInvoiceWorkareaGroup = (
  group: WorkareaGroupWithWorkareas,
  invoiceSummaries: InvoiceSummary[],
  transactions: Transaction[],
  estimatedItems: EstimatedItem[],
  actualItems: ActualItem[],
  completedWorkareaVisits: CompletedWorkareaVisit[],
  defaultGroupingOption: LineItemGroupingOption,
  defaultSalesTaxId?: number,
  defaultExemptSalesTaxId?: number,
): InvoiceWorkareaGroup => {
  const groupWorkareaIds = group.workareas.map(x => x.id)
  const alreadyInvoiced = calculateAlreadyInvoicedAmount(groupWorkareaIds, transactions)
  const completedVisits = completedWorkareaVisits.filter(x => groupWorkareaIds.includes(x.workareaId))
  const billableAmount = calculateBillableAmount(group, estimatedItems, actualItems, completedVisits)
  const deductionAmount = calculateDownPaymentDeduction(invoiceSummaries, transactions, groupWorkareaIds)
  const useQuotedPrice = group.pricingType === PricingTypes.QuotedPrice || group.pricingType === PricingTypes.PerVisit
  const amount = useQuotedPrice ? (billableAmount - alreadyInvoiced) : 0
  const unbilledAmount = (billableAmount - alreadyInvoiced)
  
  return {
    id: group.id,
    name: group.name,
    showTaxInFinalPrice: group.showTaxInFinalPrice,
    selected: true,
    amount,
    perVisitAmount: group.workareas.length > 0 ? getWorkareaQuotedPrice(group.workareas[0], estimatedItems) : 0,
    pricingType: group.pricingType,
    billableAmount,
    unbilledAmount,
    alreadyInvoiced,
    description: group.defaultInvoiceDescription ?? '',
    proposalDescription: group.customerAndInvoiceNotes,
    includeItemsSummary: false,
    itemGroupingOption: getValidGroupingOption(defaultGroupingOption, group.pricingType, group.isHidden),
    allowLineItemPerGroup: !group.isHidden,
    allowLineItemPerWorkareaItem: group.pricingType !== PricingTypes.PerVisit,
    deductionAmount,
    includeDeduction: deductionAmount > 0,
    selectedActualItemIds: [],
    selectedVisitLaborIds: [],
    selectedItemGuids: [],
    selectedVisitIds: group.pricingType === PricingTypes.PerVisit ? [...new Set(completedVisits.filter(x => !x.isInvoiced).map(x => x.visitId))] : [],
    workareaIds: groupWorkareaIds,
    selectedSalesTaxId: group.qboSalesTaxId ?? defaultSalesTaxId,
    exemptSalesTaxId: group.qboExemptSalesTaxId ?? defaultExemptSalesTaxId,
    taxRateId: group.taxRateId,
    workareas: (group.pricingType === PricingTypes.QuotedPrice && !group.isHidden) ? getPerWorkareaData(group.workareas, estimatedItems, invoiceSummaries, transactions, unbilledAmount) : [],
    hidden: group.isHidden,
    completedVisits
  }
}

const calculateAlreadyInvoicedAmount = (workareaIds: number[], transactions: Transaction[]) => {
  return sum(transactions.flatMap(x => x.elements).filter(x => workareaIds.includes(x.workareaId ?? 0)), x => x.amount)
}

export const getPerWorkareaData = (groupWorkareas: Workarea[], estimatedItems: EstimatedItem[], invoiceSummaries: InvoiceSummary[], transactions: Transaction[], unbilledAmount: number): PerWorkareaData[] => {
  let remaining = unbilledAmount
  return groupWorkareas.map(x => {
    const quotedPrice = getWorkareaQuotedPrice(x, estimatedItems)
    const alreadyInvoiced = calculateAlreadyInvoicedAmount([x.id], transactions)
    const amount = Math.min(remaining, Math.max(quotedPrice - alreadyInvoiced, 0))
    remaining -= amount
    return {
      workareaId: x.id,
      name: x.name,
      maxBillableAmount: Math.max(quotedPrice - alreadyInvoiced, 0),
      alreadyInvoiced,
      quotedPrice: quotedPrice,
      amount,
      deductionAmount: calculateDownPaymentDeduction(invoiceSummaries, transactions, [x.id])
    }
  }).filter(x => x.maxBillableAmount > 0 || x.deductionAmount > 0)
}

const calculateBillableAmount = (group: WorkareaGroupWithWorkareas, estimatedItems: EstimatedItem[], actualItems: ActualItem[], completedVisits: CompletedWorkareaVisit[]) => {
  if (group.pricingType === PricingTypes.QuotedPrice) {
    return sum(group.workareas, workarea => getWorkareaQuotedPrice(workarea, estimatedItems))
  }

  if (group.pricingType === PricingTypes.PerVisit) {
    return completedVisits.reduce((acc, x) => acc + (x.visitPrice ?? 0), 0)
  }
  
  return sum(group.workareas, workarea => workarea.pricing?.actual.customerTotal
    ?? sum(actualItems.filter(x => x.workareaId === workarea.id), x => x.totalPrice))
}

const getWorkareaQuotedPrice = (workarea: Workarea, estimatedItems: EstimatedItem[]) => {
  return workarea.quotedPrice ?? workarea.pricing?.estimated.customerTotal ?? calculateEstimatedPrice(workarea.id, estimatedItems)
}

const calculateEstimatedPrice = (workareaId: number, estimatedItems: EstimatedItem[]) => {
  const items = estimatedItems.filter(x => x.workareaId === workareaId)
  return sum(items, x => x.totalPrice)
}

export const calculateDownPaymentDeduction = (invoiceSummaries: InvoiceSummary[], transactions: Transaction[], workareaIds: number[]) => {
  const paidDownPayments = transactions
    .filter(x => invoiceSummaries.some(y => y.transactionId === x.id && y.balance === 0 && y.salesTax === 0 && y.qboSalesTax === 0))
    .flatMap(x => x.elements)
    .filter(x => x.type === 'downPayment' && workareaIds.includes(x.workareaId ?? 0))
  const invoicedDeductions = transactions
    .flatMap(x => x.elements)
    .filter(x => x.type === 'downPaymentDeduction' && workareaIds.includes(x.workareaId ?? 0))

  return sum([...paidDownPayments, ...invoicedDeductions], x => x.amount);
}

const updateGroupById = (state: NewInvoiceData, groupId: string, updater: (x: InvoiceWorkareaGroup) => InvoiceWorkareaGroup): NewInvoiceData => {
  return {
    ...state,
    workareaGroups: state.workareaGroups.map(x => x.id === groupId ? updater(x) : x)
  }
}

export const updateGroupSelection = (state: NewInvoiceData, groupId: string, selected: boolean): NewInvoiceData => {
  return updateGroupById(state, groupId, x => ({ ...x, selected }))
}

export const updateGroupShowSalesTax = (state: NewInvoiceData, groupId: string, showTaxInFinalPrice: boolean): NewInvoiceData => {
  return updateGroupById(state, groupId, x => ({ ...x, showTaxInFinalPrice }))
}

export const updateWorkareaAmount = (state: NewInvoiceData, workareaId: number, amount: number): NewInvoiceData => {
  return {
    ...state,
    workareaGroups: [...state.workareaGroups].map(x => {
      const workarea = x.workareas.find(y => y.workareaId === workareaId)
      if (workarea) {
        const otherWorkareasSum = sum(x.workareas.filter(y => y.workareaId !== workareaId), y => y.amount)
        const available = Math.max(x.unbilledAmount - otherWorkareasSum, 0)
        const workareaAmount = Math.min(amount, workarea.maxBillableAmount, available)
        const newWorkareas = [...x.workareas].map(y => y.workareaId === workareaId ? { ...y, amount: workareaAmount } : y)
        const newAmount = Math.min(sum(newWorkareas, y => y.amount), x.unbilledAmount)
        return { ...x, workareas: newWorkareas, amount: newAmount }
      }
      return x
    })
  }
}

export const updateGroupAmount = (state: NewInvoiceData, groupId: string, amount: number): NewInvoiceData => {
  return updateGroupById(state, groupId, x => {
    const newAmount = Math.min(amount, x.unbilledAmount)
    return { ...x, amount: newAmount }
  })
}

export const updateGroupDescription = (state: NewInvoiceData, groupId: string, description: string): NewInvoiceData => {
  return updateGroupById(state, groupId, x => ({ ...x, description }))
}

export const copyProposalDescription = (state: NewInvoiceData, groupId: string): NewInvoiceData => {
  return updateGroupById(state, groupId, x => ({ ...x, description: x.proposalDescription }))
}

export const canCopyProposalDescription = (group: InvoiceWorkareaGroup): boolean => {
  return !isEmptyRichText(group.proposalDescription) && group.proposalDescription !== group.description
}

export const updateGroupItemsSummary = (state: NewInvoiceData, groupId: string, includeItemsSummary: boolean): NewInvoiceData => {
  return updateGroupById(state, groupId, x => ({ ...x, includeItemsSummary }))
}

export const updateGroupItemGroupingOption = (state: NewInvoiceData, groupId: string, itemGroupingOption: number): NewInvoiceData => {
  return updateGroupById(state, groupId, x => ({ ...x, itemGroupingOption }))
}

export const updateGroupIncludeDeduction = (state: NewInvoiceData, groupId: string, includeDeduction: boolean): NewInvoiceData => {
  return updateGroupById(state, groupId, x => ({ ...x, includeDeduction }))
}

export const updateCreditCardEnabled = (state: NewInvoiceData, value: boolean): NewInvoiceData => {
  return { ...state, enableCreditCardPayments: value }
}

export const updateACHEnabled = (state: NewInvoiceData, value: boolean): NewInvoiceData => {
  return { ...state, enableACHPayments: value }
}

export const updateDueDate = (state: NewInvoiceData, value: string): NewInvoiceData => {
  return { ...state, dueDate: value }
}

export const updateShowAuthorizePaymentOnFile = (state: NewInvoiceData, value: boolean): NewInvoiceData => {
  return { ...state, showAuthorizePaymentOnFile: value, requireAuthorizePaymentOnFile: state.requireAuthorizePaymentOnFile && value }
}

export const updateRequireAuthorizePaymentOnFile = (state: NewInvoiceData, value: boolean): NewInvoiceData => {
  return { ...state, requireAuthorizePaymentOnFile: value }
}

export const updateIncludePaymentSummary = (state: NewInvoiceData, value: boolean): NewInvoiceData => {
  return { ...state, includePaymentSummary: value }
}

export const updateGroupSelectedItems = (
  state: NewInvoiceData,
  groupId: string,
  selectedItemGuids: string[],
  selectedItemIds: number[],
  selectedVisitLaborIds: string[],
  amount: number
): NewInvoiceData => {
  const updated = updateGroupById(state, groupId, x => ({ ...x, selectedItemGuids, selectedActualItemIds: selectedItemIds, selectedVisitLaborIds }))
  return updateGroupAmount(updated, groupId, amount)
}

export const updateSelectedVisits = (state: NewInvoiceData, groupId: string, selectedVisitIds: string[]): NewInvoiceData => {
  const updated = updateGroupById(state, groupId, x => ({ ...x, selectedVisitIds }))
  const group = updated.workareaGroups.find(x => x.id === groupId)
  const newAmount = group?.completedVisits.filter(x => selectedVisitIds.includes(x.visitId) && !x.isInvoiced)
    .reduce((acc, x) => acc + (x.visitPrice ?? 0), 0) ?? 0
  return updateGroupAmount(updated, groupId, newAmount)
}

export const resetWorkareaGroups = (state: NewInvoiceData, workareaGroups: InvoiceWorkareaGroup[]): NewInvoiceData => {
  return { ...state, workareaGroups }
}

export const calculateInvoiceTotal = (state: NewInvoiceData): number => {
  return sum(state.workareaGroups.filter(x => x.selected), x => x.amount)
}

export const canIncludeItemsSummary = (pricingType: PricingType): boolean =>
  pricingType !== PricingTypes.QuotedPrice && pricingType !== PricingTypes.PerVisit

export const buildInvoiceRequest = (state: NewInvoiceData, jobId: number, updatedItems: WorkareaItemBase[], invoicingSystem?: InvoicingSystem): CreateInvoiceRequest => {
  return {
    invoice: {
      projectId: jobId,
      dueDateUtc: new Date(state.dueDate).toISOString(),
      enableCreditCardPayments: state.enableCreditCardPayments,
      enableAchPayments: state.enableACHPayments,
      showAuthorizePaymentOnFile: state.showAuthorizePaymentOnFile,
      requireAuthorizePaymentOnFile: state.requireAuthorizePaymentOnFile,
      includePaymentSummary: state.includePaymentSummary,
      invoicingSystem: invoicingSystem ?? 'quickBooks',
      lineItems: state.workareaGroups.filter(x => x.selected).map(x => {
        const deductDownPayment = x.includeDeduction && x.showTaxInFinalPrice
        return {
          workareaId: x.workareaIds[0],
          childWorkareaIds: x.workareaIds.slice(1),
          description: x.description,
          name: x.name,
          taxable: x.showTaxInFinalPrice,
          amountWithoutTax: x.amount + (deductDownPayment ? x.deductionAmount : 0),
          deductDownPayment,
          includeItemsSummary: x.includeItemsSummary && canIncludeItemsSummary(x.pricingType),
          invoiceGroupingOption: x.itemGroupingOption,
          exemptSalesTaxId: x.exemptSalesTaxId ?? null,
          selectedSalesTaxId: x.selectedSalesTaxId ?? null,
          taxRateId: x.taxRateId ?? null,
          itemIds: x.selectedActualItemIds,
          laborIds: x.selectedVisitLaborIds,
          visitIds: x.selectedVisitIds,
          amountWithoutTaxPerWorkarea: x.workareas.length > 0 ? x.workareas.reduce((acc, y) => ({ ...acc, [y.workareaId]: y.amount + (deductDownPayment ? y.deductionAmount : 0) }), {}) : undefined
        }
      })
    },
    actualItemsToUpdate: updatedItems.filter(x => isActualItem(x)).map(x => x as ActualItem)
  }
}

export const updateGroupSalesTaxId = (state: NewInvoiceData, groupId: string, value: number): NewInvoiceData => {
  return updateGroupById(state, groupId, x => ({ ...x, selectedSalesTaxId: value }))
}

export const updateGroupTaxRateId = (state: NewInvoiceData, groupId: string, value: string | null | undefined): NewInvoiceData => {
  return updateGroupById(state, groupId, x => ({ ...x, taxRateId: value }))
}

export const getBillablePerVisitAmounts = (visits:{id: string, date: string}[], completedWorkareaVisits: CompletedWorkareaVisit[]): {visitId: string, date:string, visitPrice: number}[] => {
  const result = completedWorkareaVisits.filter(x => !x.isInvoiced)
    .map(x => {
      const visit = visits.find(v => v.id == x.visitId) ?? { id: 'INVALID', date: 'INVALID'}
      return { visitId: visit.id, date: visit.date, visitPrice: x.visitPrice ?? 0 }
  })
    .filter(x => x.date !== 'INVALID')

  result.sort((a, b) => a.date.localeCompare(b.date))
  return result
}

export const getDefaultGroupingForJobType = (invoicingSettings: InvoicingSettings, jobType: ProjectType): LineItemGroupingOption => {
  switch (jobType) {
    case ProjectTypes.Maintenance:
      return invoicingSettings.defaultLineItemGrouping.maintenance
    case ProjectTypes.Ticket:
      return invoicingSettings.defaultLineItemGrouping.serviceTicket
    default:
      return invoicingSettings.defaultLineItemGrouping.project
  }
}

const dayInMilliseconds = 24 * 60 * 60 * 1000

export const getDefaultDueDate = (invoicingSettings: InvoicingSettings): string => {
  return new Date(Date.now() + invoicingSettings.defaultDueInDays * dayInMilliseconds).toISOString()
}

export const shouldShowTaxRateSelector = (invoicingSystem: InvoicingSystem, group: InvoiceWorkareaGroup) => {
  return invoicingSystem === 'synkedUp' &&
    (group.showTaxInFinalPrice || group.pricingType === PricingTypes.TimeAndMaterials || group.pricingType === PricingTypes.FlatRate)
}

export const hasAutomaticTaxRateGroups = (state: NewInvoiceData, invoicingSystem: InvoicingSystem): boolean => {
  return state.workareaGroups.some(x => x.selected && shouldShowTaxRateSelector(invoicingSystem, x) && !hasValue(x.taxRateId))
}

export const isCreateBlockedByUnresolvableTaxRate = (hasAutomaticGroups: boolean, resolvedTaxRate: ResolvedTaxRate | undefined): boolean => {
  return hasAutomaticGroups && resolvedTaxRate !== undefined && !resolvedTaxRate.isResolved
}
